/**
 * API Client
 * Centralized Axios instance with interceptors and error handling
 */

import axios from "axios";
import { ENV } from "config/environment";
import { STORAGE_KEYS } from "utils/constants";
import storage from "utils/storage";

const apiClient = axios.create({
  baseURL: ENV.API_BASE_URL,
  timeout: ENV.API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (onSuccess, onFailure) => {
  refreshSubscribers.push({ onSuccess, onFailure });
};

const onTokenRefreshed = () => {
  refreshSubscribers.forEach((sub) => sub.onSuccess());
  refreshSubscribers = [];
};

const onRefreshFailed = (error) => {
  refreshSubscribers.forEach((sub) => sub.onFailure(error));
  refreshSubscribers = [];
};

const refreshAccessToken = async () => {
  try {
    const response = await axios.post(
      `${ENV.API_BASE_URL}/auth/refresh`,
      null,
      { timeout: ENV.API_TIMEOUT, withCredentials: true }
    );

    if (response.data?.success) {
      return true;
    }

    throw new Error("Token refresh failed");
  } catch (error) {
    storage.auth.clearAuth();
    throw error;
  }
};

const handleAuthFailure = () => {
  storage.auth.clearAuth();
  window.dispatchEvent(new CustomEvent("auth:refresh-failed"));
};

// Replay a request after a successful token refresh. Re-running the original
// config directly (rather than hardcoding the JSON `apiClient`) preserves the
// client that initiated it. For multipart uploads we drop the stale
// Content-Type so axios regenerates a fresh boundary for the retried FormData —
// otherwise the server can't parse the body.
const retryRequest = (originalRequest) => {
  if (originalRequest.data instanceof FormData && originalRequest.headers) {
    delete originalRequest.headers["Content-Type"];
    delete originalRequest.headers["content-type"];
  }
  return axios(originalRequest);
};

// RESPONSE INTERCEPTOR - Handle 401 with cookie-based refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const isAuthEndpoint =
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/signup") ||
        originalRequest.url?.includes("/auth/refresh");

      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          await refreshAccessToken();
          isRefreshing = false;
          onTokenRefreshed();
          return retryRequest(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshFailed(refreshError);
          handleAuthFailure();
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve, reject) => {
        subscribeTokenRefresh(
          () => resolve(retryRequest(originalRequest)),
          (err) => reject(err)
        );
      });
    }

    return Promise.reject(error);
  }
);

export const createApiClient = (config = {}) => {
  const client = axios.create({
    baseURL: ENV.API_BASE_URL,
    timeout: ENV.API_TIMEOUT,
    withCredentials: true,
    ...config,
  });

  client.interceptors.response.use(
    apiClient.interceptors.response.handlers[0].fulfilled,
    apiClient.interceptors.response.handlers[0].rejected
  );

  return client;
};

export const multipartApiClient = createApiClient({
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

export default apiClient;
